import { useState } from 'react';
import { Button, FormField, FormSheet, Input } from '@/shared/components';
import type { PropertyGroupResponse } from '@/shared/api';
import { ACCESS_CODE_MAX } from './objectEditForms';
import { useObjectUpdate } from './useObjectUpdate';

/**
 * Haustürcode des Objekts. Der hinterlegte Wert kommt nie zurück — ein leeres Feld heißt deshalb
 * „behalten", und nur „Code entfernen" löscht ihn ausdrücklich.
 */
export function ObjectAccessCodeSheet({
  group,
  onClose,
}: {
  group: PropertyGroupResponse;
  onClose: () => void;
}) {
  const [code, setCode] = useState('');
  const [remove, setRemove] = useState(false);
  const { save, saving, error } = useObjectUpdate(
    group,
    '/property-groups/$id/stammdaten',
    onClose,
  );
  const trimmed = code.trim();
  const tooLong = trimmed.length > ACCESS_CODE_MAX;
  const hasCode = group.hasBuildingAccessCode;

  return (
    <FormSheet
      title="Haustürcode"
      description="Nur nötig, wenn der Hauseingang ein Codeschloss hat. Gäste sehen den Code wie den Türcode erst 12 Stunden vor Anreise."
      dirty={remove || code.length > 0}
      saving={saving}
      canSave={remove || (trimmed.length > 0 && !tooLong)}
      error={error}
      onSubmit={() => save({ buildingAccessCode: remove ? '' : trimmed })}
      onClose={onClose}
    >
      {remove ? (
        <div role="status" className="border-line bg-card space-y-3 rounded-md border p-4 text-sm">
          <p className="text-ink">
            Der Code wird beim Speichern entfernt. Gäste sehen dann keinen Haustürcode mehr.
          </p>
          {/* Der gedrückte Knopf verschwindet; der Fokus springt auf den Weg zurück. */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="min-h-11"
            autoFocus
            onClick={() => setRemove(false)}
          >
            Doch behalten
          </Button>
        </div>
      ) : (
        <>
          {hasCode && (
            <p className="text-ink-soft text-sm">
              Ein Code ist hinterlegt. Aus Sicherheitsgründen wird er hier nicht angezeigt.
            </p>
          )}
          <FormField
            label={hasCode ? 'Neuer Code' : 'Code'}
            hint={hasCode ? 'Leer lassen, um den hinterlegten Code zu behalten.' : undefined}
            error={tooLong ? `Höchstens ${ACCESS_CODE_MAX} Zeichen` : undefined}
          >
            <Input
              value={code}
              autoFocus
              autoComplete="off"
              placeholder={hasCode ? '••••' : 'z. B. 0815'}
              onChange={(event) => setCode(event.target.value)}
            />
          </FormField>
          {hasCode && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="min-h-11"
              onClick={() => {
                setCode('');
                setRemove(true);
              }}
            >
              Code entfernen
            </Button>
          )}
        </>
      )}
    </FormSheet>
  );
}
